import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { SearchInput } from './search_input';
import { SearchResults } from './search_results';
import { BarTrigger } from './bar_trigger';
import { ResponsiveStyles, ScopedOverlayStyles, InstanceStyles, TRIGGER_CSS } from './responsive_styles';
import { overlayPortalTarget, isScopedOverlay } from '../overlay_portal';

// TileOverlay renders a trigger button that opens a fullscreen portal overlay
// showing the image-forward tile grid (SearchResults variant='tile'). The
// overlay closes on Escape, backdrop click, or the close button. Multi-mode
// widget (#5414).
export function TileOverlay({
  search,
  merged = {},
  enabled,
  demoMode,
  // The demo's per-instance event channel (item #8838); null for a customer.
  demoTransport = null,
  onResultClick,
  styleVars = {},
  hideTrigger = false,
  // Where the overlay portals. Null means document.body, as it always has.
  portalTarget = null
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Scoped, the overlay's box resolves against its host instead of the viewport
  // (item #8711). A shadow root is not a containing block, so moving the node
  // into the placement was never enough on its own -- `fixed` kept measuring the
  // viewport from inside the placement and kept blacking out the page. Per
  // decision #5067 this is still only where the overlay is laid out: the shape
  // is unchanged and it still opens on its trigger.
  const scoped = isScopedOverlay(portalTarget);
  // The open control and the close control share a corner when the overlay is
  // scoped to this mount rather than laid over the document (item #8771).
  const scopedOverlayOpen = scoped && open;

  const overlay = (
    <div
      className={`vo-search-tile-overlay vo-search-active${scoped ? ' vo-search-overlay-scoped' : ''}`}
      data-theme={merged.theme || 'light'}
      style={{ ...styleVars, position: scoped ? 'absolute' : 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column' }}
    >
      {/*
        Same standing as the modal overlay (item #8679): scoped to a placement
        this renders inside its shadow root, where the head-injected
        vo-search.css cannot reach the close button, the input or the result
        states. Page-scoped it portals to document.body and needs nothing.
      */}
      {scoped && <ScopedOverlayStyles customCss={merged.customCss} />}
      {!scoped && <InstanceStyles css={merged.customCss} />}
      {/*
        The blur matches what the modal backdrop declares in vo-search.css and
        SCOPED_OVERLAY_CSS (item #8784). It is written inline rather than added
        to either stylesheet because plan #5070 keeps the `.vo-search-tile-*`
        families deliberately unmirrored -- a rule in vo-search.css would reach
        only the page-scoped overlay and miss every scoped one. The literal
        rather than `var(--backdrop-blur)`: that token is the vo-light/vo-dark
        glass blur on the modal CARD, an unrelated effect. Prefixed twin
        included because inline React styles are not autoprefixed.
      */}
      <div
        className="vo-search-tile-overlay-backdrop"
        onClick={() => setOpen(false)}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--backdrop-color, rgba(0,0,0,0.7))',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
        }}
      />
      <div
        className="vo-search-tile-overlay-content"
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', margin: '0 auto', width: '100%', maxWidth: '1100px', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/*
          The gutter is the close control's clearance (item #8759). The titlebar
          is a flex row ending at the right of content that is width:100%, so
          with no padding the button's top and right edges ARE the overlay's --
          measured identical to the pane's on /search/display-modes/tile, which
          put the control inside the window frame's corner radius. Tight over a
          real viewport, wrong inside any rounded container, so it is fixed here
          rather than compensated on the demo page (#5082's boundary).

          16px horizontal matches the body's own gutter below, so the control
          lines up with the edge of the grid rather than floating over it. No
          bottom gutter: the input carries its own spacing.
        */}
        <div
          className="vo-search-tile-overlay-titlebar"
          style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}
        >
          <button
            className="vo-search-search-close-button"
            onClick={() => setOpen(false)}
            aria-label="Close search"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
        {enabled && search && (
          <>
            <SearchInput
              query={search.query}
              onQueryChange={search.set_query}
              onClear={search.clear_search}
              // The config's answer, as every other SearchInput host asks it
              // (item #9016). Hardcoded, this focused the input whenever the
              // overlay opened -- and SearchInput reports a focus as the
              // VISITOR taking over, which is permanent. So the landing's
              // Fullscreen Gallery demo stopped itself by opening and never
              // typed a character, on every visit. The demo sets autoFocus
              // false precisely so it never steals focus; a customer's gallery
              // ships it true and still focuses on open.
              autoFocus={merged.autoFocus}
              primaryColor={merged.primaryColor}
              inputIconColor={merged.inputIconColor}
              placeholder={merged.searchPlaceholder}
              demoMode={demoMode}
              demoTransport={demoTransport}
            />
            <div className="vo-search-tile-overlay-body" style={{ overflowY: 'auto', padding: '16px' }}>
              <SearchResults
                results={search.results}
                isLoading={search.is_loading}
                error={search.error}
                onResultClick={onResultClick}
                primaryColor={merged.primaryColor}
                displayTags={merged.displayTags}
                variant="tile"
                tileColumns={merged.tileColumns}
                showHeroImage={merged.showHeroImage}
                showExcerpts={merged.showExcerpts}
                excerptLines={merged.excerptLines}
                showThumbnails={merged.showThumbnails}
                openInNewTab={merged.openInNewTab}
                showAccessIcons={merged.showAccessIcons}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="vo-search-tile" style={{ position: 'relative' }}>
      {open && typeof document !== 'undefined' &&
        ReactDOM.createPortal(overlay, overlayPortalTarget(portalTarget))}
      {!hideTrigger && merged.shape?.trigger === 'bar' && (
        <BarTrigger merged={merged} hidden={scopedOverlayOpen} onOpen={() => setOpen(true)} />
      )}
      {!hideTrigger && merged.shape?.trigger !== 'bar' && (
        <>
          {/*
            Same standing as the overlay host's trigger (decision #5064): the
            tile grid above portals to document.body and reaches the
            head-injected vo-search.css, this button stays put, so inside a
            pinned shape's shadow root it carries its own rules or renders as a
            UA-default box.
          */}
          <ResponsiveStyles css={TRIGGER_CSS} />
          <button
            type="button"
            className="vo-search-search-modal-trigger"
            style={{
              '--primary-color': merged.primaryColor,
              ...(merged.modalTriggerIconColor ? { '--modal-trigger-icon-color': merged.modalTriggerIconColor } : {}),
              // See index.jsx: a scoped overlay is a sibling of this button, so
              // the close control lands on top of it (item #8771).
              ...(scopedOverlayOpen ? { visibility: 'hidden' } : {})
            }}
            onClick={() => setOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke={merged.modalTriggerIconColor || '#ffffff'}
              strokeWidth="2"
              width="20"
              height="20"
              style={{ display: 'block' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" fill="none" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
